"use client";

import { Field } from "@base-ui/react/field";
import { Form } from "@base-ui/react/form";
import { track } from "@vercel/analytics";
import { AnimatePresence, m } from "motion/react";
import { useRouter } from "next/navigation";
import { useRef, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import Logo from "@/icons/logo";
import { cn } from "@/lib/utils";
import { sendOtp, verifyOtp } from "./actions";
import { SubmitButton } from "./submit-button";
const inputClass = cn("mb-1 h-11 rounded-lg border bg-preview-bg px-3 text-gray-1200 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-gray-800", "border-gray-400 focus:border-gray-600 focus:ring-3 focus:ring-gray-500/35", "aria-invalid:border-red-500 aria-invalid:focus:border-red-500 aria-invalid:focus:ring-red-500/20");
const errorClass = "text-pretty text-red-500 text-sm";
type LoginStep = {
  kind: "email";
} | {
  kind: "code";
  email: string;
};
type LoginValues = Record<"email" | "code", string | undefined>;
const SIX_DIGITS = /^\d{6}$/;
const NON_DIGITS = /\D/g;
function validateCode(value: unknown) {
  const entered = String(value ?? "");
  if (entered === "" || SIX_DIGITS.test(entered)) {
    return null;
  }
  return "Enter the 6-digit code from your email.";
}
export function LoginForm() {
  const [challenge, setChallenge] = useState<LoginStep>({
    kind: "email"
  });
  const step = challenge.kind;
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, startTransition] = useTransition();
  const inFlight = useRef(false);
  const router = useRouter();
  const onSubmit = (values: LoginValues) => {
    if (inFlight.current || pending) {
      return;
    }
    inFlight.current = true;
    setErrors({});
    startTransition(async () => {
      try {
        if (challenge.kind === "email") {
          const address = values.email ?? "";
          const result = await sendOtp(address);
          if (result.error) {
            setErrors({
              email: result.error
            });
          } else {
            setChallenge({
              email: address,
              kind: "code"
            });
          }
        } else {
          const result_0 = await verifyOtp(challenge.email, values.code ?? "");
          if (result_0.error) {
            setErrors({
              code: result_0.error
            });
          } else {
            track("Login Completed");
            startTransition(() => router.push("/magazine"));
          }
        }
      } catch {
        setErrors({
          [step]: "Something went wrong. Refresh the page and try again."
        });
      } finally {
        inFlight.current = false;
      }
    });
  };
  return <div className="flex w-full max-w-90 flex-col items-center justify-center">
      <Logo className="mb-5 size-16 text-gray-1200" />
      <Heading as="h1" className="relative mb-5 w-full text-center text-gray-1200" size={3} weight="semibold">
        <AnimatePresence initial={false} mode="popLayout">
          <m.span animate={{
          filter: "blur(0px)",
          opacity: 1
        }} className="inline-block will-change-transform" exit={{
          filter: "blur(4px)",
          opacity: 0
        }} initial={{
          filter: "blur(4px)",
          opacity: 0
        }} key={step} transition={{
          bounce: 0,
          duration: 0.5,
          type: "spring"
        }}>
            {step === "email" ? "Enter your email address" : "Enter one-time password"}
          </m.span>
        </AnimatePresence>
      </Heading>
      <Form<LoginValues> className="flex w-full flex-col gap-2" errors={errors} onFormSubmit={onSubmit}>
        {step === "email" ? <Field.Root className="flex flex-col gap-1" key="email" name="email">
            <Field.Control aria-label="Email address" autoComplete="email" className={inputClass} disabled={pending} placeholder="you@example.com" required type="email" />
            <Field.Error className={errorClass} />
          </Field.Root> : <Field.Root className="flex flex-col gap-2" key="code" name="code" validate={validateCode}>
            <Field.Control aria-label="One-time password" autoComplete="one-time-code" className={inputClass} disabled={pending} inputMode="numeric" maxLength={6} onValueChange={value => {
          setCode(value.replace(NON_DIGITS, ""));
        }} placeholder="000000" required value={code} />
            <Field.Error className={errorClass} />
          </Field.Root>}
        <SubmitButton className="text-sm" pending={pending}>
          {step === "email" ? "Send code" : "Verify"}
        </SubmitButton>
        {step === "email" ? <Button className="w-full text-sm" href="/" rounded size="lg" variant="secondary">
            Back
          </Button> : <Button className="w-full text-sm" disabled={pending} onClick={() => {
        setErrors({});
        setCode("");
        setChallenge({
          kind: "email"
        });
      }} rounded size="lg" type="button" variant="secondary">
            Use a different email
          </Button>}
      </Form>
    </div>;
}